import { useCallback, useRef, type RefObject, type SyntheticEvent } from 'react'
import type { ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import { createNadDebug } from '../../utils/debugLog'
import { getSimplifiedCategory } from './simplifiedViewCategories'

const log = createNadDebug('dw:simplified-view')

/** Interaction history survives deselection, but is local to each canvas room. */
export function useLastInteractedNode(roomKey: string, nodes: RefObject<ReactFlowCanvasNode[]>) {
  const lastByRoom = useRef(new Map<string, string>())
  const eligibleNode = useCallback((id: string | undefined) => (
    id ? nodes.current?.find(node => node.id === id && getSimplifiedCategory(node.type) !== null) : undefined
  ), [nodes])

  const rememberNode = useCallback((id: string) => {
    if (!eligibleNode(id) || lastByRoom.current.get(roomKey) === id) return
    lastByRoom.current.set(roomKey, id)
    log({ event: 'simplified-view:last-node-interacted', feature: 'simplified-view', room: roomKey, node_id: id })
  }, [eligibleNode, roomKey])

  // Capture reaches controls that stop propagation and never trigger ReactFlow's
  // onNodeClick. Focus capture covers keyboard interaction with those controls.
  const captureNodeInteraction = useCallback((event: SyntheticEvent<HTMLElement>) => {
    if (!(event.target instanceof Element)) return
    const element = event.target.closest('.react-flow__node[data-id], [data-preview-node-id], [data-list-node-id]')
    const id = element?.getAttribute('data-id') ?? element?.getAttribute('data-preview-node-id') ?? element?.getAttribute('data-list-node-id')
    if (id) rememberNode(id)
  }, [rememberNode])

  const listEntryNodeId = useCallback((selection: ReadonlySet<string>) => {
    const remembered = eligibleNode(lastByRoom.current.get(roomKey))
    if (remembered) return remembered.id
    // Preserve the existing behavior for programmatic selection when there is
    // no remembered, surviving node. Never infer interaction order from a Set.
    return selection.size === 1 ? eligibleNode([...selection][0])?.id : undefined
  }, [eligibleNode, roomKey])

  return { rememberNode, captureNodeInteraction, listEntryNodeId }
}
